import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { BelgiumMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/belgiumMinimumWageConfigurationModel";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { MinimumWageCountryAdditionExpandableItemSection } from "../containers/MinimumWageCountryAdditionExpandableItemSection";
import { MinimumWageAllowanceExpandableItemSection } from "../containers/MinimumWageAllowanceExpandableItemSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { BelgiumMinimumWageExpandableItemSection } from "../containers/BelgiumMinimumWageExpandableItemSection";

import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const defaultFields: FieldName[] = [
    "includeSanitaryAddition",
    "addExtraAditionForCountry",
    "includeAdditionForNightTimeWork",
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const allowedCurrenciesInnder = [
    'EUR',
]

const allowedCurrencies = [
    'EUR',
    'PLN',
]

const country = 'belgium'

export const BelgiumMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<BelgiumMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.belgiumConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('belgiumConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
        allowanceExpandableItems: t('configuration.minimumWages.minimalAllowanceTitle'),
        countryAdditionsExpandableItems: t('configuration.minimumWages.minimalCountryAdditionTitle'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.belgiumConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, belgiumConfiguration: state })}
            handleCancel={() => setState(q.data.belgiumConfiguration)}
            dataTestId="belgium-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.belgiumConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <BelgiumMinimumWageExpandableItemSection
                        state={state}
                        setState={setState}
                        allowedCurrencies={allowedCurrenciesInnder}
                    />

                    <MinimumWageAllowanceExpandableItemSection
                        state={state}
                        setState={setState}
                        allowedCurrencies={allowedCurrencies}
                    />

                    <MinimumWageCountryAdditionExpandableItemSection
                        state={state}
                        setState={setState}
                        allowedCurrencies={allowedCurrencies}
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey='belgiumConfiguration'
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}                    
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
